import { BulletinPaie } from './FicheSalaire.types'

import { BulletinApi } from '../../api/Bulletin/BulletinApi'
import { DocumentApi } from '../../api/Document/DocumentApi'
import dayjs from 'dayjs'

export class FicheSalaireApi {
  /**
   * Récupère la liste des bulletins de paie pour un agent
   * @param identifiantEntite - L'identifiant de l'entité ESAG (number)
   * @param identifiantAgent - L'identifiant de l'agent
   * @param annee - L'année pour laquelle récupérer les bulletins (optionnel, par défaut année courante)
   * @returns Promise<BulletinPaie[]>
   */
  public static loadBulletinsPaie = async(identifiantEntite:number, identifiantAgent: string, annee?: number, silent?: boolean): Promise<BulletinPaie[]> => {
    try {
      const currentYear = new Date().getFullYear()
      const targetYear = annee || currentYear
      
      const bulletinResponse = await BulletinApi.loadBulletin(`esag=1&identifiantAgent=${identifiantAgent}&annee=${targetYear}&withDoc=1&identifiantEntite=${identifiantEntite}`, silent)


      // Extraire le tableau de bulletins de la réponse paginée
      const bulletins = bulletinResponse.data || []

      const bulletinFormatted: BulletinPaie[] = bulletins.map((element) => {
        const moisNumber = Number(element.mois) || 0
        let periode = '';
        if(element.dateDebut && element.dateFin){
          const startDate = dayjs(element.dateDebut, 'YYYY-MM-DD');
          const endDate = dayjs(element.dateFin, 'YYYY-MM-DD');
          if(!startDate.isSame(startDate.startOf('month'), 'day') || !endDate.isSame(endDate.endOf('month'), 'day')){
            periode = element.libellePeriode || `Du ${startDate.format('DD/MM/YYYY')} au ${endDate.format('DD/MM/YYYY')}`;
          }
        }
        
        // Simplifier la gestion des documents - juste passer null pour l'instant
        return {
          id: element.identifiant?.toString() || '',
          periode,
          mois: moisNumber,
          annee: element.annee || targetYear,
          dateDebut: element.dateDebut || '',
          dateFin: element.dateFin || '',
          fichierUrl: '', // À remplir avec l'URL du document si disponible
          statut: element.imprime === 1 ? 'disponible' : 'traitement' as 'disponible' | 'traitement' | 'non_disponible',
          montantNet: element.netAPayer || undefined,
          dateCoffre: element.dateCoffre || '',
          file: element.documents && element.documents.length > 0 ? {
            identifiant: element.documents[0].identifiant || 0,  
            libelle: element.documents[0].libelle || '',
            uri: '' // À remplir avec l'URL du document si disponible
          } : null
        }
      })

      // Trier les bulletins par mois (ordre chronologique)
      const bulletinsSorted = bulletinFormatted.sort((a, b) => {       
        const result = a.mois - b.mois        
        return result
      })

      return bulletinsSorted
    } catch (error) {
      console.error('❌ Erreur lors du chargement des bulletins:', error)
      return []
    }
  }

  /**
   * Télécharge un bulletin de paie spécifique et met à jour son URI
   * @param bulletin - Le bulletin de paie contenant les informations du document
   * @returns Promise<BulletinPaie> - Le bulletin mis à jour avec l'URI du document
   */
  public static downloadBulletin = async (bulletin: BulletinPaie): Promise<BulletinPaie> => {
    try {
      // Vérifier qu'il y a un document associé
      if (!bulletin.file?.identifiant) {
        console.warn('⚠️ Aucun document associé à ce bulletin')
        return bulletin
      }


      // Utiliser l'API Document pour télécharger le fichier
      const fileURL = await DocumentApi.downloadDocument(bulletin.file.identifiant)

      // fileURL contient déjà l'URL directe du fichier
      const documentUri = fileURL;
      
      // Mettre à jour le bulletin avec l'URI du document
      const updatedBulletin: BulletinPaie = {
        ...bulletin,
        fichierUrl: documentUri, // Mettre à jour l'URL du fichier
        file: bulletin.file ? {
          ...bulletin.file,
          uri: documentUri // Mettre à jour l'URI dans file
        } : null
      }

      return updatedBulletin
    } catch (error) {
      console.error('❌ Erreur lors du téléchargement du bulletin:', error)
      // Retourner le bulletin original en cas d'erreur
      return bulletin
    }
  }

  /**
   * Télécharge un document spécifique par son identifiant
   * @param documentId - L'identifiant du document (number)
   * @returns Promise<string> - URL du fichier PDF
   */
  public static downloadDocumentById = async (documentId: number): Promise<string> => {
    try {      
      const fileURL = await DocumentApi.downloadDocument(documentId)
      
      // fileURL contient déjà l'URL directe du fichier
      const documentUri = fileURL;
      
      return documentUri
    } catch (error) {
      console.error('❌ Erreur lors du téléchargement du document:', error)
      throw error
    }
  }
}